import BlogArticle from '@/components/feature/BlogArticle';

const props = {
  "slug": "moteur-volet-roulant-en-panne",
  "category": "Volet roulant",
  "seoTitle": "Moteur de volet roulant en panne : diagnostic et solutions",
  "metaDescription": "Moteur de volet roulant en panne à Lyon ? Symptômes, diagnostic (condensateur, fins de course, télécommande) et réparation ou remplacement toutes marques.",
  "h1": "Moteur de volet roulant en panne : comment diagnostiquer",
  "datePublished": "2026-07-25",
  "dateLabel": "25 juillet 2026",
  "readingTime": "7 min",
  "heroImage": "/images/dfpro-hero-2026.jpg",
  "heroAlt": "Moteur de volet roulant en panne, diagnostic à Lyon",
  "ctaTitle": "Moteur qui bourdonne ou muet ?",
  "ctaText": "DFPRO Lyon diagnostique et répare toutes marques. Devis gratuit.",
  "intro": [
    "Un volet roulant électrique qui ne répond plus à la commande, qui bourdonne sans bouger, ou qui s'arrête en pleine course : dans tous ces cas, le moteur ou son environnement électrique sont en cause. Mais attention — « le moteur est en panne » ne veut pas dire « il faut changer le moteur ». Bien souvent, c'est un composant périphérique qui est fautif.",
    "Un diagnostic méthodique permet d'éviter un remplacement coûteux et inutile. Voici les symptômes typiques d'une panne de moteur, ce que chacun révèle, et les solutions adaptées — le tout valable pour l'ensemble des grandes marques."
  ],
  "sections": [
    {
      "h2": "Comment fonctionne un moteur de volet roulant",
      "body": [
        "Le moteur est un tube logé à l'intérieur de l'axe d'enroulement. Il reçoit ses ordres soit d'un interrupteur filaire, soit d'une télécommande via un récepteur radio. Un <strong>condensateur</strong> lui fournit l'impulsion de démarrage, et des <strong>fins de course</strong> lui indiquent où s'arrêter en haut et en bas.",
        "Chacun de ces éléments peut défaillir indépendamment. C'est pourquoi le symptôme précis est si important : il pointe vers le composant à contrôler en premier."
      ]
    },
    {
      "h2": "Le volet ne réagit plus du tout",
      "body": [
        "Commencez par l'alimentation : disjoncteur, coupure, autre appareil sur le même circuit. Si le courant arrive mais que rien ne se passe, testez la commande. Sur une installation radio, une <strong>pile de télécommande usée</strong> ou une <strong>perte d'appairage</strong> avec le récepteur est fréquente.",
        "Astuce de diagnostic : si un point de commande filaire fonctionne mais pas la télécommande, le moteur est sain — c'est la télécommande ou le récepteur radio qu'il faut traiter. À l'inverse, si aucune commande ne fonctionne, on remonte vers le moteur."
      ]
    },
    {
      "h2": "Le moteur bourdonne mais ne bouge pas",
      "body": [
        "C'est la signature d'un <strong>condensateur en fin de vie</strong>. Il n'a plus assez d'énergie pour lancer le moteur, qui vibre sans tourner. Souvent, le volet fonctionne encore dans un sens (la descente, aidée par le poids) mais plus dans l'autre.",
        "Le remplacement du condensateur redonne vie au moteur dans la plupart des cas — une réparation nettement moins coûteuse qu'un moteur neuf. C'est typiquement le genre de panne où un diagnostic évite une dépense inutile."
      ]
    },
    {
      "h2": "Le volet s'arrête avant la fin",
      "body": [
        "Deux causes principales : des <strong>fins de course déréglées</strong> (le moteur croit être arrivé) ou une <strong>sécurité thermique</strong> déclenchée. Les moteurs modernes coupent quand ils chauffent trop, après plusieurs manœuvres rapprochées ou en cas d'effort anormal (tablier qui force).",
        "Un réglage des fins de course règle le premier cas. Pour le second, il faut chercher pourquoi le moteur force : tablier lourd, coulisse grippée, lame accrochée."
      ]
    },
    {
      "h2": "Réparer ou remplacer — et toutes marques",
      "body": [
        "Beaucoup de pannes se réparent sans changer le moteur : condensateur, fins de course, télécommande, récepteur. Quand le moteur est réellement grillé (aucune réaction, parfois odeur de brûlé), le <a href=\"/reparation-moteur-volet-roulant-lyon/\" class=\"text-primary-600 font-medium underline\">remplacement du moteur</a> est la solution durable.",
        "Nous intervenons sur l'ensemble des marques du marché — Somfy, Bubendorff, Nice, Simu, Profalux — et connaissons les spécificités de chacune. Ces marques sont déposées par leurs propriétaires respectifs ; DFPRO Lyon intervient en réparateur indépendant. Sur un volet ancien et manuel, une panne récurrente est aussi l'occasion d'envisager une <a href=\"/motorisation-volet-roulant-lyon/\" class=\"text-primary-600 font-medium underline\">motorisation</a> complète."
      ]
    }
  ],
  "faq": [
    {
      "q": "Comment savoir si c'est le moteur ou le condensateur ?",
      "a": "Un condensateur défaillant laisse le moteur bourdonner ou fonctionner dans un seul sens. Un moteur grillé ne réagit plus du tout et peut dégager une odeur de brûlé. Seul un test sur place, avec le matériel adéquat, permet de trancher avec certitude — c'est ce que fait notre technicien avant tout remplacement."
    },
    {
      "q": "Réparez-vous les moteurs Somfy et Bubendorff ?",
      "a": "Oui, nous intervenons sur toutes les grandes marques (Somfy, Bubendorff, Nice, Simu, Profalux) en tant que réparateur indépendant : remplacement de moteur, de condensateur, de télécommande ou de récepteur, et réglage des fins de course."
    },
    {
      "q": "Un moteur de volet roulant, ça se répare vraiment ou faut-il le changer ?",
      "a": "Souvent, ce n'est pas le moteur lui-même qui est mort mais un composant périphérique (condensateur, télécommande, fin de course). Le diagnostic permet d'identifier la vraie panne et d'éviter un remplacement inutile. Quand le moteur est réellement HS, on le remplace."
    },
    {
      "q": "Quel délai d'intervention à Lyon ?",
      "a": "Nous intervenons sous 24 à 48h à Lyon et dans la métropole, 7j/7. Devis gratuit après diagnostic et garantie 10 ans sur nos réparations. Appelez le 09 80 80 36 84."
    }
  ],
  "related": [
    {
      "title": "Volet roulant qui ne remonte plus : causes et solutions",
      "href": "/blog/volet-roulant-ne-remonte-plus/"
    },
    {
      "title": "Volet roulant bloqué : que vérifier avant d'appeler",
      "href": "/blog/volet-roulant-bloque-que-faire/"
    },
    {
      "title": "Changer la sangle d'un volet roulant",
      "href": "/blog/changer-sangle-volet-roulant/"
    }
  ]
};

export default function MoteurVoletRoulantEnPanne() {
  return <BlogArticle {...props} />;
}
